import { useQueryClient } from '@tanstack/react-query';

import { RULE_ID } from '~/common/constants';
import globalLanguage from '~/common/constants/language.json';
import useGetAccountSummaryInformation, {
  keys as assetAccountSummaryKeys,
} from '~/pages/Assets/AccountSummary/api/useGetAccountSummaryInformation';
import useSingleAsset, {
  keys as singleAssetKeys,
} from '~/pages/Assets/AssetDepositoryAccounts/api/useSingleAsset';
import { NetProceeds } from '~/pages/Assets/AssetDepositoryAccounts/enum';
import useGetAssetBankStatement, {
  keys as assetBankStatementKeys,
} from '~/pages/Assets/DepositoryBankStatement/api/useGetAssetBankStatement';
import { IncomeRuleIds } from '~/pages/IncomeCalculator/enums';
import { useSelectedAlert } from '~/stores/navigationStore';

/**
 *  hook to determine full view if the page uses the PDFViewer component
 */
const useFullPageWithPDFViewer = () => {
  const {
    ruleId,
    ruleReference,
    additionalReferences,
    displayTitle,
    ruleSubCategoryName,
  } = useSelectedAlert();
  const queryClient = useQueryClient();

  const {
    WIRE_INSTRUCTIONS,
    TITLE_COMMITMENT,
    PRE_CLOSING_STATUS_REPORT,
    CPL,
    CIL,
    SELLER_ACQUISITION,
    VALIDATE_PAYSTUB,
  } = globalLanguage.HEADER_TITLES;

  //INFO: displayTitle is different from displayTitle in GetNavigationDetails call
  const doesContainCalculatePaystubText =
    displayTitle?.includes('Calculate') && displayTitle?.includes('Paystub');

  const isWireRuleId =
    ruleId === RULE_ID.WIRE_INSTRUCTIONS_RULE &&
    displayTitle === WIRE_INSTRUCTIONS; //INFO: WireInstructions component is in React, needed for ruleId check in GlobalLayout & FormTemplate
  const isCPLRuleId =
    ruleId === RULE_ID.CPL_REVIEW_RULE &&
    (displayTitle === CPL || displayTitle === CIL);
  const isTitleCommitmentRuleId =
    ruleId === RULE_ID.TITLE_COMMITMENT_RULE &&
    (displayTitle === TITLE_COMMITMENT ||
      displayTitle === PRE_CLOSING_STATUS_REPORT);
  const isSellerAcquisitionRuleId =
    ruleId === RULE_ID.SELLER_ACQUISITION_RULE &&
    displayTitle === SELLER_ACQUISITION;
  const isRealEstateOwned = ruleSubCategoryName === 'RealEstateOwned';

  const isAssetTypeRuleWithProvidedDocument =
    ruleId === RULE_ID.ASSET_TYPE_RULE_ID &&
    !!queryClient.getQueryData<ReturnType<typeof useSingleAsset>['data']>(
      singleAssetKeys.forAsset(ruleReference?.id),
    )?.screenData?.assets?.isDocumentProvided;

  const isAssetPageWithPDFViewer =
    (ruleId === RULE_ID.ASSET_TYPE_RULE_ID &&
      additionalReferences.AssetType === NetProceeds) ||
    isAssetTypeRuleWithProvidedDocument ||
    (ruleId === RULE_ID.ASSET_BANK_STATEMENT_RULE_ID &&
      !queryClient.getQueryData<
        ReturnType<typeof useGetAssetBankStatement>['data']
      >(
        assetBankStatementKeys.forDocument(
          additionalReferences.AssetId,
          ruleReference?.id,
        ),
      )?.screenData?.llmPendingMessage) ||
    ruleId === RULE_ID.ASSET_ACCOUNT_REVIEW_RULE_ID ||
    (ruleId === RULE_ID.ASSET_ACCOUNT_SUMMARY_RULE_ID &&
      queryClient.getQueryData<
        ReturnType<typeof useGetAccountSummaryInformation>['data']
      >(assetAccountSummaryKeys.forAsset(additionalReferences.AssetId))
        ?.screenData?.asset?.isDocumentProvided);
  const isCalculatePaystubRuleId =
    ruleId === IncomeRuleIds.CalculateIncome && doesContainCalculatePaystubText;
  const isCalculatePage =
    ruleId === IncomeRuleIds.CalculateIncome &&
    additionalReferences.IsV3Active === 'True';
  const isHomeOwnerInsurance =
    ruleId === RULE_ID.HOMEOWNERS_PARENT_RULE_ID ||
    ruleId === RULE_ID.FLOOD_PARENT_RULE_ID ||
    ruleId === RULE_ID.SUPPLEMENTAL_PARENT_RULE_ID;

  const isValidatePaystubRuleId =
    ruleId === IncomeRuleIds.SmartValidateDocument &&
    displayTitle === VALIDATE_PAYSTUB;
  const isTitlePackageReviewRuleId =
    isCPLRuleId ||
    isWireRuleId ||
    isTitleCommitmentRuleId ||
    isSellerAcquisitionRuleId;
  const isPurchaseAgreementRuleId =
    ruleId === RULE_ID.PURCHASE_AGREEMENT_RULE_ID;
  const isFullPageWithPDFViewer =
    isWireRuleId ||
    isTitlePackageReviewRuleId ||
    isAssetPageWithPDFViewer ||
    isValidatePaystubRuleId ||
    isCalculatePaystubRuleId ||
    isCalculatePage ||
    isPurchaseAgreementRuleId ||
    isRealEstateOwned ||
    isHomeOwnerInsurance;

  return {
    isFullPageWithPDFViewer,
    isTitleCommitmentRuleId,
    isTitlePackageReviewRuleId,
  };
};

export default useFullPageWithPDFViewer;
